import { useState, useEffect, useRef } from 'react';
import type { ErklaerungStatus, KostenuebernahmeForm } from '@/types/erklaerung';
import { saveDraft } from '@/api/erklaerung';

interface Props {
  token: string;
  erklaerung: ErklaerungStatus;
  initialForm: Partial<KostenuebernahmeForm>;
  onNext: (form: KostenuebernahmeForm) => void;
}

function isFormComplete(f: Partial<KostenuebernahmeForm>): f is KostenuebernahmeForm {
  return !!(
    f.anteil_prozent &&
    f.zahlungsart &&
    f.zahlungsweise &&
    f.rechnung_firma?.trim() &&
    f.rechnung_ap_name?.trim() &&
    f.rechnung_email_ap?.trim() &&
    f.rechnung_strasse?.trim() &&
    f.rechnung_hausnummer?.trim() &&
    f.rechnung_plz?.trim() &&
    f.rechnung_ort?.trim()
  );
}

export function ErklaerungStep2Form({ token, erklaerung, initialForm, onNext }: Props) {
  const [form, setForm] = useState<Partial<KostenuebernahmeForm>>({
    anteil_prozent: 100,
    zahlungsart: 'einmalig',
    zahlungsweise: 'ueberweisung',
    ...initialForm,
  });
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);

  const { studi } = erklaerung;

  function update<K extends keyof KostenuebernahmeForm>(key: K, value: KostenuebernahmeForm[K] | string | null) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  // Auto-save effect
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (!isFormComplete(form)) return;
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await saveDraft(token, form as KostenuebernahmeForm);
        setSavedAt(new Date());
      } catch {
        // silent — not critical
      } finally {
        setSaving(false);
      }
    }, 1500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [form, token]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isFormComplete(form)) onNext(form);
  }

  const studienbeginnDisplay = studi.studienbeginn
    ? new Date(studi.studienbeginn).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
    : studi.studienbeginn ?? '–';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Auto-save indicator */}
      <div className="flex justify-end h-5">
        {saving && <span className="text-xs text-slate-400">Speichern…</span>}
        {!saving && savedAt && (
          <span className="text-xs text-green-600 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Entwurf gespeichert {savedAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {/* Studi Info Block */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Studierende/r</div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <span className="text-slate-500">Name:</span>{' '}
            <span className="font-medium text-slate-800">
              {[studi.anrede, studi.vorname, studi.nachname].filter(Boolean).join(' ') || '–'}
            </span>
          </div>
          <div>
            <span className="text-slate-500">Matrikel:</span>{' '}
            <span className="font-medium text-slate-800">{studi.matrikel ?? '–'}</span>
          </div>
          <div>
            <span className="text-slate-500">Studienort:</span>{' '}
            <span className="font-medium text-slate-800">{studi.studienort ?? '–'}</span>
          </div>
          <div>
            <span className="text-slate-500">Studienbeginn:</span>{' '}
            <span className="font-medium text-slate-800">{studienbeginnDisplay}</span>
          </div>
        </div>
      </div>

      {/* Section: Anteil */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Anteil der Kostenübernahme</h2>
        <div className="space-y-2">
          {([100, 50] as const).map(pct => (
            <label key={pct} className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="anteil_prozent"
                value={pct}
                checked={form.anteil_prozent === pct}
                onChange={() => update('anteil_prozent', pct)}
                className="accent-teal-600"
              />
              <span className="text-sm text-slate-800">{pct}%</span>
            </label>
          ))}
        </div>
      </section>

      {/* Section: Zahlungsart */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Zahlungsart der Studiengebühren</h2>
        <div className="space-y-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="zahlungsart"
              value="einmalig"
              checked={form.zahlungsart === 'einmalig'}
              onChange={() => update('zahlungsart', 'einmalig')}
              className="accent-teal-600"
            />
            <span className="text-sm text-slate-800">Einmalig (Zahlung der Gesamtstudiengebühr)</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="zahlungsart"
              value="monatlich"
              checked={form.zahlungsart === 'monatlich'}
              onChange={() => update('zahlungsart', 'monatlich')}
              className="accent-teal-600"
            />
            <span className="text-sm text-slate-800">Monatlich (Zahlung per Monatsrate)</span>
          </label>
        </div>
      </section>

      {/* Section: Zahlungsweise */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Zahlungsweise</h2>
        <div className="space-y-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="zahlungsweise"
              value="ueberweisung"
              checked={form.zahlungsweise === 'ueberweisung'}
              onChange={() => update('zahlungsweise', 'ueberweisung')}
              className="accent-teal-600"
            />
            <span className="text-sm text-slate-800">Überweisung (Standard)</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="zahlungsweise"
              value="sepa"
              checked={form.zahlungsweise === 'sepa'}
              onChange={() => update('zahlungsweise', 'sepa')}
              className="accent-teal-600"
            />
            <span className="text-sm text-slate-800">SEPA-Lastschrift</span>
          </label>
        </div>
      </section>

      {/* Section: Rechnungsanschrift */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-4">Rechnungsanschrift</h2>
        <div className="space-y-4">
          <Field label="Firmenname *" required>
            <input
              type="text"
              value={form.rechnung_firma ?? ''}
              onChange={e => update('rechnung_firma', e.target.value)}
              className={inputClass}
              placeholder="Muster GmbH"
            />
          </Field>
          <Field label="Adresszusatz">
            <input
              type="text"
              value={form.rechnung_adresszusatz ?? ''}
              onChange={e => update('rechnung_adresszusatz', e.target.value || null)}
              className={inputClass}
              placeholder="c/o, Gebäude, Etage …"
            />
          </Field>
          <Field label="Ansprechpartner *" required>
            <input
              type="text"
              value={form.rechnung_ap_name ?? ''}
              onChange={e => update('rechnung_ap_name', e.target.value)}
              className={inputClass}
              placeholder="Max Mustermann"
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="E-Mail Ansprechpartner *" required>
              <input
                type="email"
                value={form.rechnung_email_ap ?? ''}
                onChange={e => update('rechnung_email_ap', e.target.value)}
                className={inputClass}
                placeholder="ap@muster-gmbh.de"
              />
            </Field>
            <Field label="E-Mail Rechnungseingang">
              <input
                type="email"
                value={form.rechnung_email_invoice ?? ''}
                onChange={e => update('rechnung_email_invoice', e.target.value || null)}
                className={inputClass}
                placeholder="rechnungen@muster-gmbh.de"
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Abteilung">
              <input
                type="text"
                value={form.rechnung_abteilung ?? ''}
                onChange={e => update('rechnung_abteilung', e.target.value || null)}
                className={inputClass}
                placeholder="Buchhaltung"
              />
            </Field>
            <Field label="Telefon">
              <input
                type="tel"
                value={form.rechnung_telefon ?? ''}
                onChange={e => update('rechnung_telefon', e.target.value || null)}
                className={inputClass}
                placeholder="+49 201 …"
              />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <Field label="Straße *" required>
                <input
                  type="text"
                  value={form.rechnung_strasse ?? ''}
                  onChange={e => update('rechnung_strasse', e.target.value)}
                  className={inputClass}
                  placeholder="Musterstraße"
                />
              </Field>
            </div>
            <Field label="Hausnummer *" required>
              <input
                type="text"
                value={form.rechnung_hausnummer ?? ''}
                onChange={e => update('rechnung_hausnummer', e.target.value)}
                className={inputClass}
                placeholder="1a"
              />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="PLZ *" required>
              <input
                type="text"
                value={form.rechnung_plz ?? ''}
                onChange={e => update('rechnung_plz', e.target.value)}
                className={inputClass}
                placeholder="45127"
                maxLength={10}
              />
            </Field>
            <div className="col-span-2">
              <Field label="Ort *" required>
                <input
                  type="text"
                  value={form.rechnung_ort ?? ''}
                  onChange={e => update('rechnung_ort', e.target.value)}
                  className={inputClass}
                  placeholder="Essen"
                />
              </Field>
            </div>
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!isFormComplete(form)}
          className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg px-6 py-2.5 text-sm transition-colors flex items-center gap-2"
        >
          Weiter zu Schritt 3
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </form>
  );
}

const inputClass =
  'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
